import { Icon } from '@coast/schemas';
import { isNil } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { View } from '@shared/components/View';
import { MBIconV2 } from '@shared/components/image';
import { MBScrollView } from '@shared/components/scrollview';
import { MBSearchBar } from '@shared/components/searchBar';
import { MBTextBody2, MBTextOverlineBold } from '@shared/components/typography';
import { MBMenuRow } from '@shared/scenes/cards/bulk/components/menuRow';
import { ColorThemeContext, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';

export type LookupComponent = API.TextComponent | API.NumberComponent | API.DateComponent;

interface RelatedCardLookupMenuProps {
  components: LookupComponent[];
  selectedComponentId?: string;
  onLookupSelect(component: LookupComponent): void;
}

export function RelatedCardLookupMenu({
  components,
  selectedComponentId,
  onLookupSelect,
}: RelatedCardLookupMenuProps) {
  const colors = useContext(ColorThemeContext);
  const [filterValue, setFilterValue] = useState<string>('');
  const [filteredComponents, setFilteredComponents] = useState<LookupComponent[]>([]);

  useEffect(() => {
    setFilteredComponents(
      components.filter(c => c.label?.toLowerCase().includes(filterValue.toLowerCase().trim())),
    );
  }, [components, filterValue]);

  return (
    <View paddingBottom={2}>
      <View paddingHorizontal={3} paddingVertical={2}>
        <MBTextOverlineBold color={colors.foregroundInactive}>
          {i18n('builder.relatedCardLookup.fieldOptions')}
        </MBTextOverlineBold>
      </View>
      <View paddingHorizontal={2}>
        <MBSearchBar
          value={filterValue}
          onChangeText={val => {
            setFilterValue(!isNil(val) ? val : '');
          }}
          placeholder={i18n(`common.search`)}
        />
      </View>
      <MBScrollView style={{ maxHeight: grid(30) }}>
        <View paddingHorizontal={3} paddingTop={2} paddingBottom={1.5}>
          <MBTextBody2>
            {i18n('builder.relatedCardLookup.relatedCardLookupSelectionHelpText')}
          </MBTextBody2>
        </View>
        {filteredComponents?.map(component => {
          return (
            <RelatedCardField
              key={component.id}
              component={component}
              isSelected={component.id === selectedComponentId}
              onSelect={() => onLookupSelect(component)}
            />
          );
        })}
      </MBScrollView>
    </View>
  );
}

interface RelatedCardFieldProps {
  component: LookupComponent;
  isSelected: boolean;
  onSelect(): void;
}

function RelatedCardField({ component, isSelected, onSelect }: RelatedCardFieldProps) {
  const colors = useContext(ColorThemeContext);

  return (
    <MBMenuRow
      isHidingPressableIndicator
      onPress={onSelect}
      title={component.label}
      key={component.id}
      skipWrappingIcon
      icon={isSelected && <MBIconV2 name="check-filled" size={grid(3)} color={colors.accent} />}
      image={
        <MBIconV2 name={component.icon as Icon} color={colors.foregroundActive} size={grid(3)} />
      }
      imageStyle={{ marginRight: grid(1) }}
    />
  );
}
